import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Phone,
  Lock,
  Play,
  ArrowRight,
  Volume2,
  VolumeX,
  Award,
  Trophy,
  Sparkles,
  ChevronRight,
  MessageSquare,
  Sun,
  Sunrise,
  Moon,
  Check,
  Crown,
  Users,
  Video as VideoIcon,
  Zap,
  BookOpen,
  MapPin,
  Building2,
  Layers,
  Copy as CopyIcon,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { VIDEOS, PALETTES } from "@/config/AssetSpine";

// ============================================================
// /cierra — Million Group Movement · Sovereign Vault
// ------------------------------------------------------------
// Not the standard 6-module chassis. Custom modules for Cierra:
//   01 · Algorithm Shield   — self-owned video vault
//   02 · Grid Academy       — 3 gated cornerstone lessons
//   03 · Movement Support   — Amanda answers 440-771-8409
//   04 · Group White-Label  — every entrepreneur gets their own node
// CTA · Sovereign Node — $97/mo locked forever.
//
// Skin: PALETTES.CIERRA — "The Gold Standard" (warm gold +
// pure white + deep charcoal). Grit & Gear tactile human feel.
// ============================================================

const P = PALETTES.CIERRA;

const HERO_VIDEO_URL = VIDEOS.AMANDA_FOUNDER;
const MOVEMENT_PHONE = "(440) 771-8409";
const MOVEMENT_RATE  = "$97/mo";

const LESSONS = [
  {
    n: "01",
    title: "AI Content Factory",
    subtitle: "One shoot. Thirty pieces of content. Zero burnout.",
    body:
      "How Amanda drafts, schedules, and posts your entire monthly content stack from a single 90-minute studio day. The Movement playbook, opened.",
    duration: "38 min",
    icon: VideoIcon,
    proof: "2,140 Movement members enrolled",
  },
  {
    n: "02",
    title: "The 24/7 Lead Shield",
    subtitle: "Your entrepreneurs never hit voicemail again.",
    body:
      "How the Grid answers phones at 2am so your Movement members can sleep. Includes the exact intake script Amanda uses and the callback logic that closes the loop before dawn.",
    duration: "42 min",
    icon: ShieldCheck,
    proof: "0 lost calls · rolling 90 days",
  },
  {
    n: "03",
    title: "Owning the Last 50 Miles",
    subtitle: "The Cierra Doctrine on local dominance.",
    body:
      "Why chasing viral reach broke a decade of entrepreneurs — and how the Movement flipped it. The 50-mile radius playbook: how to own your zip, your county, and your name before you ever chase a national follower.",
    duration: "51 min",
    icon: MapPin,
    proof: "The founding lecture · Movement kickoff",
  },
];

const CALLBACK_WINDOWS = [
  { key: "morning",   label: "Morning",   sub: "8a — 11a", icon: Sunrise },
  { key: "afternoon", label: "Afternoon", sub: "12p — 4p", icon: Sun },
  { key: "evening",   label: "Evening",   sub: "5p — 7p",  icon: Moon },
];
const DAY_CHOICE = [
  { key: "today",    label: "Today",    sub: "Feb 03" },
  { key: "tomorrow", label: "Tomorrow", sub: "Feb 04" },
];

const WHITE_LABEL_NODES = [
  { name: "Cierra Lueck",          brand: "Million Group Movement",   role: "Founder · Sovereign Vault",     tone: P.PRIMARY },
  { name: "Yara Okafor",           brand: "Okafor Wealth Studio",     role: "Movement · Legacy Node",        tone: P.ACCENT },
  { name: "Devon Ríos",            brand: "Ríos Field Academy",       role: "Movement · Rural Reach Node",   tone: P.PRIMARY },
  { name: "Priya Nair",            brand: "Nair Consulting Group",    role: "Movement · Executive Node",     tone: P.ACCENT },
  { name: "August Solheim",        brand: "Solheim & Co.",            role: "Movement · Founders Node",      tone: P.PRIMARY },
  { name: "Renée Beaumont",        brand: "Beaumont Community Grid",  role: "Movement · Community Node",     tone: P.ACCENT },
];

// ============================================================
// Root
// ============================================================
export default function Cierra() {
  return (
    <div className="min-h-screen text-white relative" style={{ background: P.BG }}>
      <Backdrop />

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 space-y-12">
        <Hero />
        <BrandStripe />
        <AlgorithmShield />
        <GridAcademy />
        <MovementSupport />
        <GroupWhiteLabel />
        <SovereignCTA />
      </main>
    </div>
  );
}

// ============================================================
// Backdrop — editorial vignette with subtle gold grid
// ============================================================
function Backdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(1100px 700px at 20% -10%, ${P.PRIMARY}22, transparent 60%),` +
            `radial-gradient(900px 500px at 100% 10%, ${P.ACCENT}0A, transparent 60%),` +
            `linear-gradient(180deg, ${P.BG} 0%, ${P.BG_HI} 55%, ${P.BG} 100%)`,
        }}
      />
      <svg className="absolute inset-0 w-full h-full opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
        <filter id="c-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0 0 0 0 0.85  0 0 0 0 0.75  0 0 0 0 0.35  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#c-noise)" />
      </svg>
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            `linear-gradient(${P.PRIMARY}3D 1px, transparent 1px),` +
            `linear-gradient(90deg, ${P.PRIMARY}3D 1px, transparent 1px)`,
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
        }}
      />
      <style>{`
        @keyframes c-pulse { 0%,100%{opacity:.6} 50%{opacity:1} }
        @keyframes c-ring { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(1.9);opacity:0} }
        @keyframes c-shine { 0%{transform:translateX(-120%)} 100%{transform:translateX(220%)} }
        @keyframes c-ping { 0%{transform:scale(1);opacity:.8} 70%{transform:scale(1.9);opacity:0} 100%{transform:scale(1.9);opacity:0} }
      `}</style>
    </div>
  );
}

// ============================================================
// HERO — Amanda 4.0 addressing Cierra directly
// ============================================================
function Hero() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <section data-testid="ci-hero">
      <div
        className="text-[10px] uppercase tracking-[0.32em] font-mono flex items-center gap-2 mb-3"
        style={{ color: P.PRIMARY }}
      >
        <Crown size={11} /> Million Group Movement · Sovereign Vault · Founder Greeting
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white mb-4 max-w-4xl">
        Cierra — this is the vault where your{" "}
        <span style={{ color: P.PRIMARY }} className="italic">
          movement lives
        </span>{" "}
        when the algorithm doesn&apos;t.
      </h1>
      <p className="text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: P.ACCENT_DEEP }}>
        Amanda walks you and every entrepreneur in the group through
        exactly what Sovereign Vault does — how the lessons stay locked,
        how the leads stay answered, and how the Movement outlives the
        feed.
      </p>

      <div className="relative mx-auto w-full mt-6">
        <div
          className="absolute -inset-1 rounded-sm pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${P.PRIMARY}66, ${P.ACCENT}33, ${P.PRIMARY}66)`,
            filter: "blur(18px)",
            opacity: 0.6,
          }}
        />
        <div
          className="relative rounded-sm overflow-hidden border"
          style={{
            borderColor: `${P.PRIMARY}80`,
            boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 60px ${P.PRIMARY}33`,
            background: "#000",
          }}
        >
          <video
            ref={videoRef}
            src={HERO_VIDEO_URL}
            autoPlay
            muted
            playsInline
            controls
            data-testid="ci-hero-video-el"
            className="w-full h-auto block bg-black"
          />
          <button
            type="button"
            onClick={toggle}
            data-testid="ci-hero-mute"
            className="absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur transition-colors"
            style={{
              background: `${P.BG}CC`,
              border: `1px solid ${P.PRIMARY}66`,
              color: P.PRIMARY,
            }}
          >
            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
            <span className="text-[10px] uppercase tracking-[0.22em] font-mono">
              {muted ? "Tap to unmute" : "Muted off"}
            </span>
          </button>
          <div
            className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur border"
            style={{ background: `${P.BG}CC`, borderColor: `${P.PRIMARY}66` }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}` }}
            />
            <span className="text-[9px] uppercase tracking-[0.22em] font-mono" style={{ color: P.INK }}>
              Amanda · Live · Sovereign Vault
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// BRAND STRIPE
// ============================================================
function BrandStripe() {
  return (
    <section
      className="rounded-sm border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(90deg, ${P.PRIMARY}18 0%, ${P.BG}99 60%, ${P.ACCENT}0A 100%)`,
      }}
      data-testid="ci-brand-stripe"
    >
      <div
        className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
        style={{ borderColor: `${P.PRIMARY}88`, background: `${P.PRIMARY}22`, color: P.PRIMARY }}
      >
        <Crown size={20} />
      </div>
      <div className="flex-1">
        <div
          className="text-[10px] uppercase tracking-[0.32em] font-mono mb-1 flex flex-wrap items-center gap-x-3 gap-y-1"
          style={{ color: P.ACCENT }}
        >
          <span>The Million Group Movement · Sovereign Vault Edition</span>
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border"
            style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}14`, color: P.PRIMARY }}
            data-testid="ci-founder-badge"
          >
            Founder · <span style={{ color: P.INK }}>Cierra Lueck</span>
          </span>
        </div>
        <div className="font-display text-white text-lg sm:text-xl leading-tight">
          The Movement running on the Grid — every lesson filed, every
          entrepreneur onboarded,{" "}
          <span style={{ color: P.PRIMARY }} className="italic">
            every legacy sealed against the feed.
          </span>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Shared section banner
// ============================================================
function SectionBanner({ kicker, title, blurb, icon: Icon, rightPill, rightSlot }) {
  return (
    <div
      className="px-5 sm:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-b"
      style={{
        borderColor: "rgba(255,255,255,0.06)",
        background: `linear-gradient(90deg, ${P.PRIMARY}12 0%, ${P.BG}80 60%, ${P.ACCENT}0A 100%)`,
      }}
    >
      <div
        className="w-10 h-10 rounded-sm border flex items-center justify-center shrink-0"
        style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>
          {kicker}
        </div>
        <div className="font-display text-white text-lg sm:text-xl leading-tight mt-0.5">{title}</div>
        {blurb && (
          <p className="mt-2 text-[12px] sm:text-[13px] leading-relaxed max-w-3xl" style={{ color: P.MUTED }}>
            {blurb}
          </p>
        )}
      </div>
      {rightPill && (
        <span
          className="hidden md:inline text-[10px] uppercase tracking-[0.24em] font-mono px-3 py-1.5 rounded-sm shrink-0"
          style={{ background: `${P.PRIMARY}12`, border: `1px solid ${P.PRIMARY}55`, color: P.PRIMARY }}
        >
          {rightPill}
        </span>
      )}
      {rightSlot && <div className="shrink-0">{rightSlot}</div>}
    </div>
  );
}

// ============================================================
// 01 · ALGORITHM SHIELD — animated vault visualization
// ============================================================
function AlgorithmShield() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.PRIMARY}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid="ci-algorithm-shield"
    >
      <SectionBanner
        kicker="Module 01 · The Algorithm Shield"
        title="Your videos live in the Vault. Meta can't touch them."
        blurb="The Sovereign Vault is a self-owned video library — hosted on the Grid, indexed by Amanda, delivered on your domain. When Instagram changes the rules, your entrepreneurs still find your library. When Reels goes dark, the Vault stays lit."
        icon={ShieldCheck}
        rightPill="Feed-Proof · Domain-Owned"
      />

      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
        {/* Vault visual */}
        <div className="lg:col-span-2 flex items-center justify-center">
          <VaultVisual />
        </div>

        {/* Talking points */}
        <div className="lg:col-span-3 space-y-3">
          {[
            {
              tag: "Self-Owned",
              title: "Your library lives on your domain — not a platform's.",
              body:
                "Cierra.com/vault or a subdomain the Movement controls. No algorithm decides who sees it. Ever.",
            },
            {
              tag: "Amanda-Indexed",
              title: "Every lesson tagged, searchable, and re-served.",
              body:
                "Amanda categorizes each upload — topic, length, guest, transcript. Members find any lesson in one search.",
            },
            {
              tag: "Legacy-Sealed",
              title: "The Vault outlives the feed cycle.",
              body:
                "TikTok goes dark. Instagram pivots. Your Movement still walks in tomorrow, logs in, and picks up where they left off.",
            },
          ].map((r, i) => (
            <div
              key={i}
              className="rounded-sm border p-4 flex items-start gap-3"
              style={{
                borderColor: `${P.PRIMARY}44`,
                background: `${P.BG_HI}A6`,
              }}
              data-testid={`ci-shield-point-${i}`}
            >
              <div
                className="w-8 h-8 rounded-sm border flex items-center justify-center shrink-0"
                style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
              >
                <Lock size={14} />
              </div>
              <div className="flex-1">
                <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>{r.tag}</div>
                <div className="font-display text-white text-base leading-tight mt-0.5">{r.title}</div>
                <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.MUTED }}>{r.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VaultVisual() {
  // Concentric shield rings around the Movement's video vault icon.
  return (
    <div className="relative w-full max-w-[280px] aspect-square" data-testid="ci-vault-visual">
      {/* Outer pulsing rings */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute inset-0 rounded-full border-2"
          style={{
            borderColor: `${P.PRIMARY}${["55", "77", "AA"][i]}`,
            transform: `scale(${1 - i * 0.18})`,
            animation: `c-ring ${3 + i}s ease-out infinite`,
            animationDelay: `${i * 0.7}s`,
          }}
        />
      ))}
      {/* Static inner ring */}
      <div
        className="absolute rounded-full border-2 flex items-center justify-center"
        style={{
          inset: "22%",
          borderColor: P.PRIMARY,
          background: `radial-gradient(circle at center, ${P.BG_HI} 0%, ${P.BG} 70%)`,
          boxShadow: `0 0 40px ${P.PRIMARY}55, 0 0 0 1px ${P.PRIMARY}44 inset`,
        }}
      >
        <div
          className="w-16 h-16 rounded-sm border flex items-center justify-center relative"
          style={{
            borderColor: P.PRIMARY,
            background: `linear-gradient(160deg, ${P.PRIMARY}33 0%, ${P.BG}CC 60%)`,
            boxShadow: `0 0 20px ${P.PRIMARY}66 inset`,
          }}
        >
          <Lock size={26} style={{ color: P.PRIMARY }} />
          <span
            className="absolute -top-2 -right-2 text-[9px] font-mono uppercase tracking-[0.24em] px-1.5 py-0.5 rounded-sm border"
            style={{ borderColor: P.PRIMARY, background: P.BG, color: P.PRIMARY }}
          >
            Vault
          </span>
        </div>
      </div>

      {/* Corner labels — social platforms locked out */}
      {[
        { top: "6%",    left: "8%",   label: "Meta"       },
        { top: "6%",    right: "8%",  label: "TikTok"     },
        { bottom: "6%", left: "8%",   label: "YouTube"    },
        { bottom: "6%", right: "8%",  label: "X · Threads"},
      ].map((tag, i) => (
        <div
          key={i}
          className="absolute text-[8px] uppercase tracking-[0.28em] font-mono px-1.5 py-0.5 rounded-sm"
          style={{
            ...tag,
            color: P.MUTED,
            background: `${P.BG}CC`,
            border: `1px solid ${P.MUTED}44`,
          }}
        >
          {tag.label} · locked out
        </div>
      ))}
    </div>
  );
}

// ============================================================
// 02 · GRID ACADEMY PREVIEW — 3 gated lessons
// ============================================================
function GridAcademy() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.ACCENT}08 0%, ${P.BG}F2 100%)`,
      }}
      data-testid="ci-grid-academy"
    >
      <SectionBanner
        kicker="Module 02 · The Grid Academy · Sovereign Vault"
        title="Three cornerstone lessons — locked to your Movement."
        blurb="The founding curriculum of the Million Group Movement. Watch the first 90 seconds free. The full lesson unlocks the moment your entrepreneur joins the Vault."
        icon={BookOpen}
        rightPill="3 Lessons · Locked"
      />

      <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {LESSONS.map((l) => (
          <LessonCard key={l.n} lesson={l} />
        ))}
      </div>

      <div className="px-5 sm:px-6 pb-6">
        <div
          className="rounded-sm border px-4 py-3 flex items-center gap-3"
          style={{ borderColor: `${P.PRIMARY}44`, background: `${P.PRIMARY}0F` }}
        >
          <Sparkles size={14} style={{ color: P.PRIMARY }} />
          <div className="text-[11px] leading-relaxed" style={{ color: P.INK }}>
            <span className="uppercase tracking-[0.22em] font-mono mr-2" style={{ color: P.PRIMARY }}>Academy ·</span>
            Every Movement member unlocks all three cornerstone lessons on day one. New drops land in the Vault the first Monday of each month.
          </div>
        </div>
      </div>
    </section>
  );
}

function LessonCard({ lesson }) {
  const Icon = lesson.icon;
  return (
    <div
      className="rounded-sm border overflow-hidden group flex flex-col"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(160deg, ${P.PRIMARY}0F 0%, ${P.BG_HI}E6 65%)`,
      }}
      data-testid={`ci-lesson-${lesson.n}`}
    >
      {/* Gated thumbnail */}
      <div className="relative aspect-video border-b" style={{ borderColor: "rgba(255,255,255,0.05)", background: `${P.BG}CC` }}>
        <svg viewBox="0 0 320 180" className="w-full h-full">
          <defs>
            <linearGradient id={`ci-lesson-bg-${lesson.n}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"  stopColor={P.PRIMARY} stopOpacity="0.35" />
              <stop offset="100%" stopColor={P.PRIMARY} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <rect width="320" height="180" fill={`url(#ci-lesson-bg-${lesson.n})`} />
          {/* Faint waveform bars */}
          <g stroke={P.PRIMARY} strokeWidth="1.2" opacity="0.6">
            {[...Array(28)].map((_, k) => {
              const x = 12 + k * 11;
              const h = 20 + ((k * 7 + Number(lesson.n) * 5) % 40);
              return <line key={k} x1={x} y1={160 - h} x2={x} y2={160} />;
            })}
          </g>
          <circle cx="160" cy="80" r="24" fill="none" stroke={P.PRIMARY} strokeWidth="1.5" />
        </svg>

        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(10,10,10,0.30)" }}>
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: `${P.BG}CC`, border: `1.5px solid ${P.PRIMARY}88`, color: P.PRIMARY }}
          >
            <Play size={22} />
          </div>
        </div>

        {/* Lock ribbon */}
        <div
          className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-sm border"
          style={{ borderColor: `${P.PRIMARY}88`, background: `${P.BG}CC`, color: P.PRIMARY }}
        >
          <Lock size={11} />
          <span className="text-[9px] uppercase tracking-[0.24em] font-mono">Members Only</span>
        </div>

        <div
          className="absolute top-3 left-3 px-2 py-1 rounded-sm text-[9px] uppercase tracking-[0.24em] font-mono border"
          style={{ borderColor: `${P.ACCENT}44`, background: `${P.BG}CC`, color: P.ACCENT }}
        >
          <Icon size={10} className="inline mr-1" /> Lesson {lesson.n}
        </div>

        <div
          className="absolute bottom-3 right-3 text-[9px] font-mono uppercase tracking-[0.24em]"
          style={{ color: P.MUTED }}
        >
          {lesson.duration}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display text-white text-lg leading-tight">{lesson.title}</div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-mono mt-1" style={{ color: P.PRIMARY }}>
          {lesson.subtitle}
        </div>
        <p className="mt-3 text-[12px] leading-relaxed flex-1" style={{ color: P.MUTED }}>
          {lesson.body}
        </p>

        <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="text-[9px] uppercase tracking-[0.24em] font-mono flex items-center gap-1.5" style={{ color: P.ACCENT_DEEP }}>
            <Users size={10} style={{ color: P.PRIMARY }} /> {lesson.proof}
          </div>
          <button
            type="button"
            data-testid={`ci-lesson-unlock-${lesson.n}`}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-mono px-2.5 py-1.5 rounded-sm border transition-transform active:scale-[0.98]"
            style={{ borderColor: `${P.PRIMARY}88`, background: `${P.PRIMARY}14`, color: P.PRIMARY }}
          >
            Unlock <ChevronRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 03 · MOVEMENT SUPPORT — Intake Shield · 440-771-8409
// ============================================================
function MovementSupport() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.PRIMARY}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid="ci-movement-support"
    >
      <SectionBanner
        kicker="Module 03 · Movement Support Line"
        title={`Amanda answers every Movement call — ${MOVEMENT_PHONE}.`}
        blurb="Your entrepreneurs never hit voicemail. Amanda picks up as the Movement's front-desk, books the intake, files the request in your Vault, and texts a confirmation card before the call ends."
        icon={Phone}
        rightPill="24/7 · Movement Support"
      />

      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <PhoneCard />
        </div>
        <div className="lg:col-span-2">
          <CallMeLater />
        </div>
      </div>
    </section>
  );
}

function PhoneCard() {
  const [copied, setCopied] = useState(false);
  const raw = "440-771-8409";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard may be blocked — silently ignore
    }
  };
  return (
    <div
      className="rounded-sm border overflow-hidden h-full flex flex-col"
      style={{
        borderColor: `${P.PRIMARY}66`,
        background: `linear-gradient(180deg, ${P.BG_HI}E6 0%, ${P.BG}F2 100%)`,
      }}
      data-testid="ci-phone-card"
    >
      <div
        className="px-5 py-3 border-b flex items-center justify-between"
        style={{ borderColor: "rgba(255,255,255,0.06)", background: `${P.PRIMARY}0F` }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-sm border flex items-center justify-center"
            style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
          >
            <Phone size={14} />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>
              Movement Support · Direct
            </div>
            <div className="font-display text-white text-sm">Ring Amanda from anywhere</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.ACCENT_DEEP }}>
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}`, animation: "c-pulse 2s ease-in-out infinite" }}
          />
          Live now
        </div>
      </div>

      <div className="p-6 sm:p-8 flex-1 flex flex-col items-start justify-center gap-4">
        <div className="text-[10px] uppercase tracking-[0.32em] font-mono" style={{ color: P.ACCENT_DEEP }}>
          Movement Support Line
        </div>
        <a
          href={`tel:+1${raw.replace(/-/g, "")}`}
          data-testid="ci-phone-tel"
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight"
          style={{ color: P.PRIMARY, textShadow: `0 0 40px ${P.PRIMARY}33` }}
        >
          {MOVEMENT_PHONE}
        </a>
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <a
            href={`tel:+1${raw.replace(/-/g, "")}`}
            data-testid="ci-phone-call"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
            style={{
              background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
              border: `1px solid ${P.PRIMARY}`,
              color: P.BG,
              boxShadow: `0 0 22px -6px ${P.PRIMARY}`,
            }}
          >
            <Phone size={13} /> Call Amanda Now
          </a>
          <button
            type="button"
            onClick={copy}
            data-testid="ci-phone-copy"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono border transition-colors"
            style={{
              borderColor: `${P.PRIMARY}66`,
              background: copied ? `${P.PRIMARY}22` : `${P.BG}A6`,
              color: copied ? P.PRIMARY : P.INK,
            }}
          >
            <CopyIcon size={13} /> {copied ? "Copied" : "Copy Number"}
          </button>
        </div>
        <div className="text-[11px] leading-relaxed pt-2" style={{ color: P.MUTED }}>
          Configured for <span style={{ color: P.PRIMARY }}>Movement Support</span> — every inbound call routes to
          Amanda first. Members hear a real voice within two rings, get their request logged in the Vault, and receive a text confirmation before hanging up.
        </div>
      </div>
    </div>
  );
}

function CallMeLater() {
  const [step, setStep] = useState("idle");
  const [day, setDay] = useState("today");
  const [win, setWin] = useState("afternoon");
  const [phone, setPhone] = useState("");
  const chosenWin = CALLBACK_WINDOWS.find((w) => w.key === win) || CALLBACK_WINDOWS[1];
  const chosenDay = DAY_CHOICE.find((d) => d.key === day) || DAY_CHOICE[0];
  const readyToSend = phone.replace(/\D/g, "").length >= 7;
  const send = () => readyToSend && setStep("confirmed");
  const reset = () => { setStep("idle"); setPhone(""); };

  return (
    <div
      className="rounded-sm border overflow-hidden relative h-full flex flex-col"
      style={{
        borderColor: `${P.ACCENT}55`,
        background: `linear-gradient(180deg, ${P.ACCENT}0A 0%, ${P.BG}F2 55%, ${P.PRIMARY}0F 100%)`,
        boxShadow: `0 24px 60px -24px ${P.PRIMARY}55`,
      }}
      data-testid="ci-callback"
    >
      <div
        className="px-5 py-3 border-b flex items-center gap-3"
        style={{ borderColor: "rgba(255,255,255,0.06)", background: `${P.ACCENT}0A` }}
      >
        <div
          className="w-8 h-8 rounded-sm border flex items-center justify-center"
          style={{ borderColor: `${P.ACCENT}66`, background: `${P.ACCENT}14`, color: P.ACCENT }}
        >
          <MessageSquare size={14} />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.ACCENT }}>
            No-Miss Guarantee
          </div>
          <div className="font-display text-white text-sm">Call Me Later</div>
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        {step === "idle" && (
          <>
            <p className="text-sm leading-relaxed" style={{ color: P.INK }}>
              Between meetings? On a live? Tap the button. Amanda picks the moment,
              sends a text, and rings back on <span style={{ color: P.PRIMARY }}>your</span> window — not the Movement&apos;s.
            </p>
            <button
              type="button"
              onClick={() => setStep("picking")}
              data-testid="ci-callback-open"
              className="mt-5 w-full flex items-center justify-center gap-3 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] transition-transform active:scale-[0.99]"
              style={{
                background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
                border: `1px solid ${P.PRIMARY}`,
                color: P.BG,
                boxShadow: `0 0 28px -6px ${P.PRIMARY}90`,
              }}
            >
              <Phone size={16} /> Book My Callback
            </button>
            <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.MUTED }}>
              <Sparkles size={11} style={{ color: P.PRIMARY }} />
              Text confirmation · sub-30 seconds
            </div>
          </>
        )}

        {step === "picking" && (
          <div data-testid="ci-callback-picker">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono mb-3" style={{ color: P.PRIMARY }}>
              Pick your window
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {DAY_CHOICE.map((d) => {
                const active = day === d.key;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setDay(d.key)}
                    className="rounded-sm border py-2 px-3 text-left transition-all"
                    style={{
                      borderColor: active ? P.PRIMARY : "rgba(255,255,255,0.12)",
                      background: active ? `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}80 70%)` : `${P.BG}99`,
                      boxShadow: active ? `0 0 18px -6px ${P.PRIMARY}` : "none",
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: active ? P.PRIMARY : P.MUTED }}>{d.label}</div>
                    <div className="text-[9px] font-mono mt-0.5" style={{ color: P.MUTED }}>{d.sub}</div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {CALLBACK_WINDOWS.map((w) => {
                const active = win === w.key;
                const Icon = w.icon;
                return (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => setWin(w.key)}
                    className="rounded-sm border py-3 px-2 text-center transition-all"
                    style={{
                      borderColor: active ? P.ACCENT : "rgba(255,255,255,0.12)",
                      background: active ? `linear-gradient(160deg, ${P.ACCENT}18 0%, ${P.BG}80 70%)` : `${P.BG}99`,
                      boxShadow: active ? `0 0 18px -6px ${P.ACCENT}` : "none",
                    }}
                  >
                    <Icon size={16} className="mx-auto" style={{ color: active ? P.ACCENT : P.MUTED }} />
                    <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: active ? P.ACCENT : P.INK }}>{w.label}</div>
                    <div className="text-[9px] font-mono" style={{ color: P.MUTED }}>{w.sub}</div>
                  </button>
                );
              })}
            </div>
            <label className="block text-[10px] uppercase tracking-[0.24em] font-mono mb-1.5" style={{ color: P.ACCENT }}>
              <MessageSquare size={11} className="inline mr-1" /> Your Phone (Amanda texts the confirmation)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(440) 555-0142"
              data-testid="ci-callback-phone"
              className="w-full bg-black/60 border rounded-sm px-4 py-3 text-white text-sm focus:outline-none min-h-[44px] transition-colors"
              style={{ borderColor: readyToSend ? `${P.PRIMARY}80` : "rgba(255,255,255,0.12)" }}
              maxLength={40}
              autoComplete="tel"
            />
            <button
              type="button"
              onClick={send}
              disabled={!readyToSend}
              data-testid="ci-callback-send"
              className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 rounded-sm text-sm font-display uppercase tracking-[0.24em] disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.99]"
              style={{
                background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
                border: `1px solid ${P.PRIMARY}`,
                color: P.BG,
                boxShadow: readyToSend ? `0 0 26px -6px ${P.PRIMARY}90` : "none",
              }}
            >
              <ArrowRight size={14} /> Send My Callback Request
            </button>
            <button type="button" onClick={() => setStep("idle")} className="mt-3 w-full text-[10px] uppercase tracking-[0.24em] font-mono py-2" style={{ color: P.MUTED }}>
              ← Back
            </button>
          </div>
        )}

        {step === "confirmed" && (
          <div
            className="rounded-sm border p-5 relative overflow-hidden flex-1 flex flex-col justify-center"
            style={{
              borderColor: P.PRIMARY,
              background: `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}B3 60%)`,
              boxShadow: `0 0 34px -8px ${P.PRIMARY}`,
            }}
            data-testid="ci-callback-confirmed"
          >
            <div className="flex items-center gap-3">
              <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-full" style={{ background: P.PRIMARY, color: P.BG }}>
                <Check size={22} strokeWidth={3} />
                <span className="absolute inset-0 rounded-full" style={{ background: P.PRIMARY, animation: "c-ping 2s cubic-bezier(0,0,0.2,1) infinite" }} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: P.PRIMARY }}>You&apos;re on the book</div>
                <div className="font-display text-white text-lg leading-tight mt-0.5">
                  Amanda will call you {chosenDay.label.toLowerCase()}, <span style={{ color: P.PRIMARY }}>{chosenWin.label.toLowerCase()}</span>.
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: P.INK }}>
              Confirmation text on the way to <span style={{ color: P.PRIMARY }}>{phone || "your phone"}</span>. Amanda rings from <span style={{ color: P.PRIMARY }}>{MOVEMENT_PHONE}</span>.
            </p>
            <button type="button" onClick={reset} className="mt-5 self-start text-[10px] uppercase tracking-[0.24em] font-mono px-3 py-2 rounded-sm border" style={{ borderColor: "rgba(255,255,255,0.14)", color: P.INK, background: `${P.BG}A6` }}>
              Book Another Window <ChevronRight size={11} className="inline" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// 04 · GROUP WHITE-LABEL — every member gets their own node
// ============================================================
function GroupWhiteLabel() {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.PRIMARY}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid="ci-white-label"
    >
      <SectionBanner
        kicker="Module 04 · Group White-Label"
        title="Every Movement member gets their own Sovereign Vault."
        blurb="The Million Group Movement isn't just Cierra's Vault — it's the launch pad. Every entrepreneur in the group unlocks a white-labeled Grid node with their name, their palette, their phone line, their Vault. The infrastructure is shared. The brand is 100% theirs."
        icon={Layers}
        rightPill="Shared Rails · Owned Brand"
      />

      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {WHITE_LABEL_NODES.map((n, i) => (
            <WhiteLabelTile key={i} node={n} index={i} />
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-sm border p-4 space-y-3" style={{ borderColor: `${P.PRIMARY}44`, background: `${P.PRIMARY}0F` }}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: P.PRIMARY }}>
              <Building2 size={11} /> How the White-Label Works
            </div>
            {[
              "Cierra hosts the Movement — one Sovereign Vault, one 440 line, one Academy library.",
              "Each entrepreneur activates their own Grid node — separate URL, separate palette, separate phone.",
              "The Academy lessons cascade in — every member's node inherits the cornerstone curriculum.",
              "Amanda scales — one AI utility, every Movement front-desk, zero training overhead.",
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-6 h-6 rounded-sm border flex items-center justify-center shrink-0 mt-0.5"
                  style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
                >
                  <span className="text-[10px] font-mono">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="text-[13px] leading-relaxed" style={{ color: P.INK }}>{line}</div>
              </div>
            ))}
          </div>

          <div
            className="rounded-sm border p-5 flex flex-col justify-between"
            style={{
              borderColor: `${P.ACCENT}88`,
              background: `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG_HI}D9 70%)`,
              boxShadow: `0 0 32px -12px ${P.PRIMARY}`,
            }}
          >
            <div>
              <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>Movement Rate</div>
              <div className="font-display text-3xl sm:text-4xl leading-none mt-2" style={{ color: P.INK }}>
                {MOVEMENT_RATE}
                <span className="text-[10px] uppercase tracking-[0.28em] font-mono ml-2" style={{ color: P.ACCENT_DEEP }}>per node</span>
              </div>
              <p className="mt-3 text-[12px] leading-relaxed" style={{ color: P.MUTED }}>
                Locked forever. One rate for every Movement member — no seat surcharge, no tier games.
              </p>
            </div>
            <a
              href="#sovereign-cta"
              data-testid="ci-white-label-cta"
              className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
              style={{
                background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
                border: `1px solid ${P.PRIMARY}`,
                color: P.BG,
                boxShadow: `0 0 22px -6px ${P.PRIMARY}`,
              }}
            >
              <Crown size={13} /> Reserve My Node
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhiteLabelTile({ node, index }) {
  const isFounder = index === 0;
  return (
    <div
      className="rounded-sm border p-4 relative overflow-hidden group"
      style={{
        borderColor: isFounder ? P.PRIMARY : `${node.tone}44`,
        background: isFounder
          ? `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG_HI}E6 65%)`
          : `linear-gradient(160deg, ${node.tone}0A 0%, ${P.BG_HI}E6 65%)`,
        boxShadow: isFounder ? `0 0 22px -8px ${P.PRIMARY}` : "none",
      }}
      data-testid={`ci-node-tile-${index}`}
    >
      {isFounder && (
        <div
          className="absolute top-2 right-2 text-[8px] uppercase tracking-[0.28em] font-mono px-1.5 py-0.5 rounded-sm border"
          style={{ borderColor: P.PRIMARY, background: `${P.BG}CC`, color: P.PRIMARY }}
        >
          Founder
        </div>
      )}
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: `${node.tone}66`, background: `${node.tone}18`, color: node.tone }}
        >
          <Crown size={15} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: node.tone }}>{node.role}</div>
          <div className="font-display text-white text-sm leading-tight">{node.brand}</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.MUTED }}>
          <Users size={10} className="inline mr-1" style={{ color: node.tone }} /> {node.name}
        </div>
        <ChevronRight size={12} style={{ color: node.tone }} className="group-hover:translate-x-0.5 transition-transform" />
      </div>
    </div>
  );
}

// ============================================================
// SOVEREIGN CTA — Founder Node
// ============================================================
function SovereignCTA() {
  return (
    <section
      id="sovereign-cta"
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}88`,
        background: `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}D9 45%, ${P.ACCENT}0A 100%)`,
        boxShadow: `0 40px 100px -30px ${P.PRIMARY}66, 0 0 0 1px ${P.PRIMARY}22 inset`,
      }}
      data-testid="ci-sovereign-cta"
    >
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.PRIMARY}, ${P.ACCENT}, ${P.PRIMARY}, transparent)` }} />
      <div
        className="absolute inset-y-0 w-40 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${P.PRIMARY}22, transparent)`,
          animation: "c-shine 4.6s ease-in-out infinite",
        }}
      />

      <div className="relative p-6 sm:p-10">
        <div className="text-[10px] uppercase tracking-[0.36em] font-mono flex items-center gap-2 mb-3" style={{ color: P.PRIMARY }}>
          <Trophy size={12} /> Sovereign Node · Cierra Doctrine
        </div>

        <div
          className="inline-flex items-center gap-3 rounded-sm border px-4 sm:px-5 py-3 mb-6"
          style={{
            borderColor: P.PRIMARY,
            background: `linear-gradient(90deg, ${P.PRIMARY_DEEP}22 0%, ${P.PRIMARY}22 60%, ${P.ACCENT}12 100%)`,
            boxShadow: `0 0 24px -4px ${P.PRIMARY}80`,
          }}
        >
          <div
            className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
            style={{ borderColor: P.PRIMARY, background: `${P.PRIMARY}30`, color: P.PRIMARY }}
          >
            <Award size={17} />
          </div>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.ACCENT }}>Movement Rate · Locked Forever</div>
            <div className="font-display text-white text-base sm:text-lg leading-tight">
              {MOVEMENT_RATE} · one seat, one Vault, zero surcharge
            </div>
          </div>
        </div>

        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight max-w-3xl">
          Protect Your Legacy —{" "}
          <span style={{ color: P.PRIMARY }} className="italic">
            Join the Grid for {MOVEMENT_RATE}.
          </span>
        </h2>
        <p className="mt-3 text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: P.MUTED }}>
          One rate for the Movement. Locked forever. Your Vault. Your Academy.
          Your entrepreneurs. Amanda answering every call. The Grid running
          the last 50 miles — while you build the next chapter.
        </p>

        <div className="mt-6 rounded-sm border p-4 grid grid-cols-1 md:grid-cols-3 gap-3" style={{ borderColor: `${P.PRIMARY}44`, background: `${P.BG_HI}A6` }}>
          {[
            { icon: Lock,        label: "Algorithm Shield",     body: "Your library, your domain, forever." },
            { icon: BookOpen,    label: "Grid Academy",         body: "3 cornerstone lessons + monthly drops." },
            { icon: Layers,      label: "Group White-Label",    body: "Every member gets their own branded node." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-sm border flex items-center justify-center shrink-0"
                  style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
                >
                  <Icon size={14} />
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.28em] font-mono" style={{ color: P.PRIMARY }}>{f.label}</div>
                  <div className="text-[12px] leading-relaxed mt-0.5" style={{ color: P.INK }}>{f.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <Link
            to="/induction?vertical=cierra"
            data-testid="ci-sovereign-cta-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] transition-transform active:scale-[0.99]"
            style={{
              background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
              border: `1px solid ${P.PRIMARY}`,
              color: P.BG,
              boxShadow: `0 0 32px -6px ${P.PRIMARY}`,
            }}
          >
            <Trophy size={15} /> Protect My Legacy · {MOVEMENT_RATE} <ArrowRight size={15} />
          </Link>
          <div className="text-[10px] uppercase tracking-[0.24em] font-mono flex items-center gap-2" style={{ color: P.MUTED }}>
            <Zap size={11} style={{ color: P.PRIMARY }} />
            Only Movement Founders · Rate never rises
          </div>
        </div>
      </div>
    </section>
  );
}
